import { EnfluxTerm } from "Stores/superStore/ingestion";

import type { CreateTermRequest, TermDto } from "./client";

/**
 * Marks a zoneless timestamp as UTC.
 *
 * platform-api types its timestamps as `LocalDateTime`, so they arrive as bare
 * wall-clock strings (`2025-08-31T23:00:00`). The legacy API returns the same
 * instants with an explicit offset (`...+0000`). The terms table renders dates
 * with `new Date(value).toISOString()`, and `new Date` reads a zoneless string
 * as *local* time, so west of UTC the same row shows a different day depending
 * on which API answered. Both APIs read the same rows, so the zoneless value is
 * the instant the legacy API labels UTC; say so explicitly.
 */
function asUtc(dateTime: string): string {
    return /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateTime) ? dateTime : `${dateTime}Z`;
}

/**
 * Maps platform-api's camelCase `TermDto` onto the snake_case `EnfluxTerm` the
 * terms table and form already consume, so reads can move to the new API
 * without touching a single component.
 *
 * Notes on the shape gap:
 * - `school_id` is absent from the DTO because the request is already scoped by
 *   school, so the caller's school id is threaded back in here.
 * - `id`, `term_order` and `dates_verified` are optional on the DTO and
 *   required on `EnfluxTerm`, so they fall back to the values the legacy API
 *   returns for an unset field.
 * - `is_active` is carried across rather than defaulted: the Active Terms
 *   toggle (EAI-5644) reads it, and dropping it renders every term as off.
 */
export function termDtoToEnfluxTerm(dto: TermDto, schoolId: string): EnfluxTerm {
    return {
        id: dto.id ?? 0,
        school_id: schoolId,
        name: dto.name,
        term: dto.term,
        term_type: dto.termType,
        calendar_year: dto.calendarYear,
        term_order: dto.termOrder ?? 0,
        acad_year: dto.acadYear ?? "",
        start_date: asUtc(dto.startDate),
        end_date: asUtc(dto.endDate),
        dates_verified: dto.datesVerified ?? false,
        is_active: dto.isActive ?? false
    };
}


/** The fields a term write carries, in the shape the UI already holds them. */
export type TermWriteFields = Pick<
    EnfluxTerm,
    "name" | "term_type" | "calendar_year" | "acad_year" | "term_order" | "start_date" | "end_date"
> & { dates_verified?: boolean; is_active?: boolean };

/**
 * Renders a timestamp as platform-api's zoneless `LocalDateTime`.
 *
 * Two shapes reach this. The term form produces a space-separated local
 * wall-clock string (`2025-08-31 23:59:59.999`), which is exactly what the
 * legacy API is given and stores. A term read back from platform-api carries
 * the `Z` that {@link termDtoToEnfluxTerm} added. Both are reduced to the same
 * bare wall clock, so a term written through either API keeps the same value.
 */
function toLocalDateTime(value: string): string {
    return value.trim().replace(" ", "T").replace(/(?:Z|[+-]\d{2}:?\d{2})$/, "");
}

/**
 * Maps a term into platform-api's write body.
 *
 * platform-api's PUT is a **full replace**: every core field on
 * `UpdateTermRequest` is required, unlike the legacy PUT which filters the body
 * down to the fields it was sent. Callers must therefore merge their changes
 * over the whole existing term, or the fields they left out are cleared.
 *
 * `term` is intentionally absent from the request type: the server derives it
 * from `termType` and `calendarYear`, so it is not a client field.
 */
export function enfluxTermToTermRequest(fields: TermWriteFields): CreateTermRequest {
    return {
        name: fields.name,
        termType: fields.term_type,
        calendarYear: fields.calendar_year,
        acadYear: fields.acad_year,
        termOrder: fields.term_order,
        startDate: toLocalDateTime(fields.start_date),
        endDate: toLocalDateTime(fields.end_date),
        datesVerified: fields.dates_verified ?? false,
        isActive: fields.is_active ?? false
    };
}
